import { format } from "date-fns";
import { formatWait } from '@/lib/formatDate';
import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  RefreshCw, Users, LayoutDashboard, X, MoreHorizontal, ArrowLeft, BarChart3, Monitor,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { StatusChip } from '@/components/ui/status-chip';
import { cn } from '@/lib/utils';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from '@/components/ui/dropdown-menu';
import { toast } from '@/hooks/use-toast';
import { ToastAction } from '@/components/ui/toast';
import { useAuth } from '@/context/AuthContext';
import { usePermission } from '@/hooks/usePermission';
import {
  getDoctors, getQueueToday, getAppointmentsByDateRange, getMissedFollowUps, getAllBillingInvoices, type FollowUpDue,
  queueCheckin, queueWalkin, queueCancel, queueReorder, queueMoveToDoctor, queueReturnWithReport, isReportReturn, updateAppointment,
  getBillingServices, createBillingInvoice,
  recordInvoicePayment,
  type QueueEntry, type QueueTodayResponse,
} from '@/lib/api';

import QueueAddSearch from '@/components/FrontDesk/QueueAddSearch';
import PatientContextPanel from '@/components/FrontDesk/PatientContextPanel';
import PatientDrawer from '@/components/FrontDesk/PatientDrawer';
import MedicalBillingModal from '@/components/Patient/MedicalBillingModal';
import { PaymentRecordModal } from '@/components/Billing/BillingModals';
import PrintDepositReceiptModal, { type DepositReceipt } from '@/components/Billing/PrintDepositReceiptModal';
import PatientFormModal from '@/components/Patient/PatientFormModal';
import AddAppointmentModal from '@/components/Appointment/AddAppointmentModal';
import PrintPrescriptionPreviewModal from '@/components/Prescription/PrintPrescriptionPreviewModal';
import PrintBillPreviewModal from '@/components/Billing/PrintBillPreviewModal';
import { usePageTitle } from "@/hooks/usePageTitle";
import VitalsWarningChips, { vitalsWarnings } from '@/components/Vitals/VitalsWarningChips';
import { MissedFollowUps } from '@/components/Home/DoctorDay';

// ─── Types ───────────────────────────────────────────────────────────────────

interface Doctor { id: string; name: string }

interface Appointment {
  id: string;
  patient_id: string;
  date: string;
  time: string;
  reason: string;
  status: string;
  doctor_id?: string;
  // The booking carries the patient basics — no lookup per patient
  patient_name?: string;
  patient_phone?: string;
  patient_age?: number;
  patient_gender?: string;
}

interface AppointmentRow extends Appointment {
  patientName: string;
  patientPhone: string;
  patientAge?: number;
  patientGender?: string;
}

type QueueEntryEx = QueueEntry & { doctorName: string; doctorId: string };

type SelectedItem =
  | { kind: 'queue'; entry: QueueEntryEx }
  | { kind: 'appointment'; apt: AppointmentRow };

// ─── Helpers ─────────────────────────────────────────────────────────────────

function drName(name: string) {
  return name.startsWith('Dr.') ? name : `Dr. ${name}`;
}

function formatTime(time: string) {
  if (!time) return '';
  const [h, m] = time.split(':').map(Number);
  return `${h % 12 || 12}:${String(m).padStart(2, '0')} ${h >= 12 ? 'PM' : 'AM'}`;
}

function waitColor(mins: number | null | undefined) {
  if (!mins) return '';
  if (mins >= 30) return 'text-destructive';
  if (mins >= 15) return 'text-warning';
  return 'text-muted-foreground';
}

// True when a scheduled time (HH:MM, today) is within the next 30 minutes or already due
function isDueSoon(time: string) {
  if (!time) return false;
  const [h, m] = time.split(':').map(Number);
  const t = new Date(); t.setHours(h, m, 0, 0);
  return t.getTime() - Date.now() <= 1_800_000;
}

// ─── Component ───────────────────────────────────────────────────────────────

export default function FrontDeskCommandCenter() {
  usePageTitle('Front desk');
  const navigate = useNavigate();
  const { currentUser } = useAuth();
  const { can } = usePermission();
  const clinicId = currentUser?.clinic_id ?? '';

  // ── Core data ──────────────────────────────────────────────────────────────
  const [doctors, setDoctors] = useState<Doctor[]>([]);
  // A bill taken with an advance: the receipt for the advance, shown once the bill is saved
  const [depositReceipt, setDepositReceipt] = useState<DepositReceipt | null>(null);
  const [selectedDoctorId, setSelectedDoctorId] = useState<string>('all');
  const [queues, setQueues] = useState<Record<string, QueueTodayResponse>>({});
  const [appointments, setAppointments] = useState<Appointment[]>([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);

  // ── Selection ──────────────────────────────────────────────────────────────
  const [selected, setSelected] = useState<SelectedItem | null>(null);

  // ── Modals ─────────────────────────────────────────────────────────────────
  const [billingPatient, setBillingPatient] = useState<any>(null);
  const [billingServices, setBillingServices] = useState<any[]>([]);
  const [billingLoading, setBillingLoading] = useState(false);
  const [collectInvoice, setCollectInvoice] = useState<any>(null);
  const [editPatient, setEditPatient] = useState<any>(null);
  const [bookPatient, setBookPatient] = useState<any>(null);
  const [missedFollowUps, setMissedFollowUps] = useState<FollowUpDue[]>([]);
  // Today's bills, to show who has paid among the patients seen (only with billing:read)
  const [todayInvoices, setTodayInvoices] = useState<any[]>([]);
  const [showAllDone, setShowAllDone] = useState(false);
  const [showRegisterModal, setShowRegisterModal] = useState(false);
  const [registerSearch, setRegisterSearch] = useState('');
  const [registerDoctorId, setRegisterDoctorId] = useState<string | null>(null);
  const [printRx, setPrintRx] = useState<any>(null);
  const [printInvoice, setPrintInvoice] = useState<any>(null);
  const [drawerPatientId, setDrawerPatientId] = useState<string | null>(null);
  const [checkingInId, setCheckingInId] = useState<string | null>(null);
  const [queueActionId, setQueueActionId] = useState<string | null>(null);
  const [confirmRemoveId, setConfirmRemoveId] = useState<string | null>(null);
  // Bumped after a bill or payment, so the panel's dues and "Collect" buttons aren't left stale
  const [panelVersion, setPanelVersion] = useState(0);

  const pollingRef = useRef<ReturnType<typeof setInterval> | null>(null);

  // ── Data fetching ──────────────────────────────────────────────────────────

  const fetchQueues = useCallback(async (docList: Doctor[]) => {
    if (!clinicId || docList.length === 0) return;
    const today = format(new Date(), "yyyy-MM-dd");
    const [results, inv] = await Promise.all([
      Promise.allSettled(docList.map(d => getQueueToday(d.id, clinicId))),
      can('billing:read') ? getAllBillingInvoices(clinicId, { dateFrom: today, dateTo: today, limit: 500 }).catch(() => null) : Promise.resolve(null),
    ]);
    setTodayInvoices(Array.isArray(inv) ? inv.filter((i: any) => i.status !== 'cancelled') : []);
    const next: Record<string, QueueTodayResponse> = {};
    results.forEach((r, i) => {
      if (r.status === 'fulfilled' && r.value) next[docList[i].id] = r.value;
    });
    setQueues(next);
  }, [clinicId, can]);

  const loadAll = useCallback(async () => {
    if (!clinicId) return;
    try {
      const today = format(new Date(), "yyyy-MM-dd");
      const [docList, apts, missed] = await Promise.all([
        getDoctors(),
        getAppointmentsByDateRange(today, today, clinicId),
        getMissedFollowUps().catch(() => null),
      ]);
      setMissedFollowUps(missed ?? []);

      const docs: Doctor[] = docList ?? [];
      const aptsArr: Appointment[] = apts ?? [];

      setDoctors(docs);
      setAppointments(aptsArr);
      setSelectedDoctorId(prev => prev);

      await fetchQueues(docs);
    } catch {
      toast({ title: 'Error', description: 'Failed to load front desk data', variant: 'destructive' });
    } finally {
      setLoading(false);
    }
  }, [clinicId, fetchQueues]);

  useEffect(() => {
    loadAll();
  }, [loadAll]);

  useEffect(() => {
    // The desk leaves this open all day: poll only while it's on screen, and catch up on return
    pollingRef.current = setInterval(() => {
      if (document.visibilityState === 'visible') fetchQueues(doctors);
    }, 30000);
    const onVisible = () => { if (document.visibilityState === 'visible') fetchQueues(doctors); };
    document.addEventListener('visibilitychange', onVisible);
    return () => {
      if (pollingRef.current) clearInterval(pollingRef.current);
      document.removeEventListener('visibilitychange', onVisible);
    };
  }, [doctors, fetchQueues]);

  const handleRefresh = async () => {
    setRefreshing(true);
    await loadAll();
    setRefreshing(false);
    toast({ title: 'Refreshed' });
  };

  // ── Derived data ───────────────────────────────────────────────────────────

  const activeDoctors = useMemo(() =>
    selectedDoctorId === 'all' ? doctors : doctors.filter(d => d.id === selectedDoctorId),
  [doctors, selectedDoctorId]);

  const allEntries = useMemo((): QueueEntryEx[] => {
    const entries: QueueEntryEx[] = [];
    activeDoctors.forEach(doc => {
      const q = queues[doc.id];
      if (!q) return;
      q.entries.forEach(e => entries.push({ ...e, doctorName: doc.name, doctorId: doc.id }));
    });
    return entries;
  }, [queues, activeDoctors]);

  const inConsultation = useMemo(() =>
    allEntries.filter(e => e.status === 'in_consultation'),
  [allEntries]);

  const onHold = useMemo(() =>
    allEntries.filter(e => e.status === 'on_hold'),
  [allEntries]);

  // One doctor: their queue order (priority moves included). All doctors: each queue numbers its own
  // order, so mixing them by it puts a later arrival first; show priority, then who came first.
  const waiting = useMemo(() =>
    allEntries.filter(e => e.status === 'waiting').sort((a, b) =>
      selectedDoctorId !== 'all'
        ? a.sortOrder - b.sortOrder
        : Number(b.isPriority) - Number(a.isPriority) || (a.checkedInAt ?? '').localeCompare(b.checkedInAt ?? '')),
  [allEntries, selectedDoctorId]);

  const completed = useMemo(() =>
    allEntries.filter(e => e.status === 'completed').sort((a, b) =>
      (b.consultationEndAt ?? '').localeCompare(a.consultationEndAt ?? '')),
  [allEntries]);

  // A seen patient's bill today: on their visit, or for them without a visit link. Undefined without billing:read
  const billOf = (e: QueueEntryEx): BillState | undefined => {
    if (!can('billing:read')) return undefined;
    const bills = todayInvoices.filter(i => e.visitId && i.visit_id ? i.visit_id === e.visitId : i.patient_id === e.patientId);
    if (bills.length === 0) return 'unbilled';
    const due = bills.reduce((s, i) => s + Math.max(0, Number(i.balance_due) || 0), 0);
    return due > 0 ? due : 'paid';
  };
  const unpaid = (e: QueueEntryEx) => { const b = billOf(e); return b !== undefined && b !== 'paid'; };
  // Done folds to the last few, but a patient who hasn't paid stays in view
  const DONE_SHOWN = 3;
  const shownDone = showAllDone ? completed : completed.filter((e, i) => i < DONE_SHOWN || unpaid(e));
  const toBill = completed.filter(unpaid).length;

  const shownMissed = selectedDoctorId === 'all' ? missedFollowUps : missedFollowUps.filter(f => f.doctor_id === selectedDoctorId);

  const upcoming: AppointmentRow[] = useMemo(() => {
    const filtered = appointments.filter(a => {
      if (a.status !== 'scheduled') return false;
      if (selectedDoctorId !== 'all' && a.doctor_id && a.doctor_id !== selectedDoctorId) return false;
      return true;
    });
    return filtered
      .map(a => ({
        ...a,
        patientName: a.patient_name ?? 'Unknown',
        patientPhone: a.patient_phone ?? '',
        patientAge: a.patient_age,
        patientGender: a.patient_gender,
      }))
      .sort((a, b) => a.time.localeCompare(b.time));
  }, [appointments, selectedDoctorId]);

  const stats = useMemo(() => {
    return {
      waiting: waiting.length,
      inConsult: inConsultation.length,
      onHold: onHold.length,
      completed: completed.length,
      unpaid: completed.filter(() => false).length,
    };
  }, [waiting, inConsultation, onHold, completed]);

  // ── Selected patient info ──────────────────────────────────────────────────

  const selectedPatientId = selected
    ? selected.kind === 'queue'
      ? (selected.entry.patientId ?? null)
      : selected.apt.patient_id
    : null;

  const selectedPatientName = selected
    ? selected.kind === 'queue'
      ? selected.entry.patientName
      : selected.apt.patientName
    : '';

  const selectedQueueEntry = useMemo((): QueueEntryEx | null => {
    if (!selectedPatientId) return null;
    // The row clicked (kept fresh by polling), not the patient's first token of the day — looked up in
    // every doctor's queue, so a patient just moved to a doctor outside the tab shows their new token
    if (selected?.kind === 'queue') {
      for (const d of doctors) {
        const e = queues[d.id]?.entries.find(x => x.id === selected.entry.id);
        if (e) return { ...e, doctorName: d.name, doctorId: d.id };
      }
      return selected.entry;
    }
    // A booking: the patient's live token if they have one, else their latest
    const mine = allEntries.filter(e => e.patientId === selectedPatientId && e.status !== 'cancelled');
    return mine.find(e => e.status !== 'completed') ?? mine[mine.length - 1] ?? null;
  }, [allEntries, selectedPatientId, selected, doctors, queues]);

  // ── Queue actions ──────────────────────────────────────────────────────────

  const handleCheckIn = async (apt: AppointmentRow) => {
    setCheckingInId(apt.id);
    try {
      const entry = await queueCheckin(apt.id);
      toast({ title: 'Checked in', description: entry ? `Token #${entry.tokenNumber} · ${apt.patientName}` : apt.patientName });
      await loadAll();
    } catch (err: any) {
      toast({ title: 'Error', description: err?.message ?? 'Check-in failed', variant: 'destructive' });
    } finally {
      setCheckingInId(null);
    }
  };

  const handlePriority = async (entry: QueueEntry) => {
    setQueueActionId(entry.id);
    try {
      await queueReorder(entry.id, true);
      await fetchQueues(doctors);
      toast({ title: 'Moved to front', description: `${entry.patientName} is now priority` });
    } catch (err: any) {
      toast({ title: 'Error', description: err?.message ?? 'Failed', variant: 'destructive' });
    } finally {
      setQueueActionId(null);
    }
  };

  // Their doctor had to leave, or the patient asked for another: next token in that doctor's queue
  const handleMove = async (entry: QueueEntry, doctorId: string) => {
    setQueueActionId(entry.id);
    try {
      const moved = await queueMoveToDoctor(entry.id, doctorId);
      await fetchQueues(doctors);
      const to = doctors.find(d => d.id === doctorId);
      toast({ title: 'Moved', description: `${entry.patientName} is now #${moved?.tokenNumber} with ${drName(to?.name ?? '')}` });
    } catch (err: any) {
      toast({ title: "Couldn't move", description: err?.message ?? 'Failed', variant: 'destructive' });
    } finally {
      setQueueActionId(null);
    }
  };

  // The doctor had to leave: everyone waiting moves, in their order, to the next tokens of another doctor
  const handleMoveAll = async (doctorId: string) => {
    const to = doctors.find(d => d.id === doctorId);
    let moved = 0;
    try {
      for (const entry of waiting) { await queueMoveToDoctor(entry.id, doctorId); moved++; }
      toast({ title: 'Moved', description: `${moved} waiting patient${moved === 1 ? '' : 's'} moved to ${drName(to?.name ?? '')}` });
    } catch (err: any) {
      toast({ title: `Moved ${moved} of ${waiting.length}`, description: err?.message ?? 'Failed', variant: 'destructive' });
    } finally {
      await fetchQueues(doctors);
    }
  };

  const handleReturnWithReport = async (entry: QueueEntryEx) => {
    setQueueActionId(entry.id);
    try {
      await queueReturnWithReport(entry.id);
      await fetchQueues(doctors);
      toast({ title: 'Back with report', description: `${entry.patientName} is next for ${drName(entry.doctorName)}` });
    } catch (err: any) {
      toast({ title: 'Error', description: err?.message ?? 'Failed', variant: 'destructive' });
    } finally {
      setQueueActionId(null);
    }
  };

  const handleRemoveFromQueue = async (entry: QueueEntry) => {
    setConfirmRemoveId(null);
    setQueueActionId(entry.id);
    try {
      await queueCancel(entry.id);
      // A report return goes back to done on the server; its booking stays completed
      if (entry.appointmentId && !isReportReturn(entry)) {
        await updateAppointment({ id: entry.appointmentId, status: 'scheduled' }).catch(() => {});
      }
      if (selected?.kind === 'queue' && selected.entry.id === entry.id) setSelected(null);
      await loadAll();
      toast({ title: 'Removed', description: `${entry.patientName} removed from queue` });
    } catch (err: any) {
      toast({ title: 'Error', description: err?.message ?? 'Failed', variant: 'destructive' });
    } finally {
      setQueueActionId(null);
    }
  };

  // ── Billing helpers ────────────────────────────────────────────────────────

  const openNewInvoice = async (patient: any) => {
    setBillingPatient(patient);
    setBillingLoading(true);
    try {
      const svcs = await getBillingServices(clinicId, currentUser?.doctor_id);
      setBillingServices(Array.isArray(svcs) ? svcs : (svcs as any)?.services ?? []);
    } catch {
      setBillingServices([]);
    } finally {
      setBillingLoading(false);
    }
  };

  const handleCreateInvoice = async (invoiceData: any) => {
    if (!billingPatient) return;
    try {
      const effectiveDoctorId = selectedDoctorId !== 'all' ? selectedDoctorId : doctors[0]?.id ?? currentUser?.doctor_id ?? '';
      const created = await createBillingInvoice({
        patient_id:      billingPatient.id,
        doctor_id:       invoiceData.doctorId || effectiveDoctorId,
        // The token being looked at, so a second visit today doesn't take this bill
        visit_id:        selectedQueueEntry?.patientId === billingPatient.id ? selectedQueueEntry.visitId ?? undefined : undefined,
        clinic_id:       clinicId,
        invoice_date:    invoiceData.invoiceDate || undefined,
        items:           invoiceData.items,
        discount_amount: invoiceData.discount    || 0,
        discount_type:   invoiceData.discount > 0 ? invoiceData.discountType : undefined,
        discount_reason: invoiceData.discountReason,
        tax_amount:      invoiceData.taxAmount   || 0,
        notes:           invoiceData.notes       || undefined,
        payments:                     invoiceData.payments    || [],
        balance_forward:              invoiceData.balance_forward              || 0,
        balance_forward_settle:       invoiceData.balance_forward_settle       || 0,
        balance_forward_payment_mode: invoiceData.balance_forward_payment_mode || undefined,
        deposit_amount:               invoiceData.depositAmount      || undefined,
        deposit_payment_mode:         invoiceData.depositPaymentMode || undefined,
      });
      // An advance taken with the bill: its receipt is one click away in the message
      const receipt: DepositReceipt | null = invoiceData.depositAmount ? {
        patient_id: billingPatient.id, clinic_id: clinicId, doctor_id: invoiceData.doctorId || effectiveDoctorId || undefined,
        amount: invoiceData.depositAmount, payment_mode: invoiceData.depositPaymentMode,
        receipt_number: created?.deposit_receipt_number,
      } : null;
      toast({ title: 'Invoice created', description: `Bill created for ${billingPatient.name}${receipt ? ` · ₹${receipt.amount.toLocaleString('en-IN')} kept as advance` : ''}`,
              duration: receipt ? 10000 : undefined, // long enough to reach the button
              action: receipt ? <ToastAction altText="Print advance receipt" onClick={() => setDepositReceipt(receipt)}>Print advance receipt</ToastAction> : undefined });
      setBillingPatient(null);
      setPanelVersion(v => v + 1);
      fetchQueues(doctors);
    } catch (err: any) {
      toast({ title: 'Error', description: err?.message ?? 'Failed', variant: 'destructive' });
    }
  };

  const handleCollectPayment = async (amount: number, method: string, meta?: { reference_number?: string; upi_app?: string; bank_name?: string }) => {
    if (!collectInvoice) return;
    const modeMap: Record<string, string> = {
      cash: 'cash', upi: 'upi', card: 'card', cheque: 'cheque',
      netbanking: 'netbanking', 'bank transfer': 'netbanking',
      insurance: 'other', other: 'other', advance: 'advance',
    };
    try {
      await recordInvoicePayment(collectInvoice.id, {
        amount,
        payment_mode: (modeMap[method?.toLowerCase()] ?? 'cash') as any,
        payment_date: new Date().toISOString(),
        reference_number: meta?.reference_number || undefined,
        upi_app: meta?.upi_app || undefined,
        bank_name: meta?.bank_name || undefined,
      });
      toast({ title: 'Payment recorded', description: `₹${amount} collected` });
      setCollectInvoice(null);
      setPanelVersion(v => v + 1);
      fetchQueues(doctors);
    } catch (err: any) {
      toast({ title: 'Error', description: err?.message ?? 'Failed', variant: 'destructive' });
    }
  };

  // ── Register new patient handler ───────────────────────────────────────────

  const handleRegisterNew = (search: string, doctorId: string | null) => {
    setRegisterSearch(search);
    setRegisterDoctorId(doctorId);
    setShowRegisterModal(true);
  };

  const handleRegisterSuccess = async (newPatient: any) => {
    setShowRegisterModal(false);
    const effectiveDoctorId = registerDoctorId ?? (selectedDoctorId !== 'all' ? selectedDoctorId : doctors[0]?.id);
    if (!effectiveDoctorId) return;
    const doctor = doctors.find(d => d.id === effectiveDoctorId);
    try {
      await queueWalkin(newPatient.id, effectiveDoctorId, clinicId, false);
      toast({ title: 'Registered & added to queue', description: doctor ? `${newPatient.name} · ${drName(doctor.name)}` : newPatient.name });
      await loadAll();
    } catch {
      toast({ title: 'Registered', description: `${newPatient.name} registered. Add them to queue manually.` });
    }
  };

  // ── Helpers ────────────────────────────────────────────────────────────────

  const isSelectedEntry = (id: string) =>
    selected?.kind === 'queue' && selected.entry.id === id;

  const isSelectedApt = (id: string) =>
    selected?.kind === 'appointment' && selected.apt.id === id;

  // ── Render ─────────────────────────────────────────────────────────────────

  if (loading) {
    return (
      <div className="h-[calc(100dvh-var(--topbar-height,60px))] flex flex-col" aria-busy="true" aria-label="Loading front desk">
        <div className="flex-none border-b border-border px-4 py-3 space-y-3">
          <div className="flex gap-5">
            {[0, 1, 2, 3, 4].map(i => <div key={i} className="h-9 w-16 rounded bg-muted animate-pulse" />)}
          </div>
          <div className="h-6 w-72 max-w-full rounded-full bg-muted/70 animate-pulse" />
        </div>
        <div className="flex-1 flex min-h-0">
          <div className="w-full md:w-60 lg:w-72 md:border-r border-border p-3 space-y-2">
            {[0, 1, 2, 3, 4, 5].map(i => <div key={i} className="h-12 rounded-lg bg-muted/60 animate-pulse" />)}
          </div>
          <div className="hidden md:block flex-1 p-6">
            <div className="h-40 rounded-2xl bg-muted/40 animate-pulse" />
          </div>
        </div>
      </div>
    );
  }

  const effectiveDoctorId = selectedDoctorId !== 'all' ? selectedDoctorId : null;
  // Tabs for the doctors with patients today (queue or bookings); all of them before anyone has
  const busyDoctors = doctors.filter(d => d.id === selectedDoctorId
    || queues[d.id]?.entries.some(e => e.status !== 'cancelled')
    || appointments.some(a => a.doctor_id === d.id && a.status !== 'cancelled' && a.status !== 'no_show'));
  const tabDoctors = busyDoctors.length > 0 ? busyDoctors : doctors;

  const figure = (value: React.ReactNode) => <span className="font-semibold tabular-nums text-foreground">{value}</span>;
  const statusParts: React.ReactNode[] = [
    <span key="date">{new Date().toLocaleDateString('en-IN', { weekday: 'long', day: 'numeric', month: 'long' })}</span>,
    <span key="scheduled">{figure(upcoming.length)} scheduled</span>,
    <span key="waiting">{figure(stats.waiting)} waiting</span>,
    <span key="consult">{figure(stats.inConsult)} in consultation</span>,
    ...(stats.onHold > 0 ? [<span key="hold">{figure(stats.onHold)} on hold</span>] : []),
    <span key="done">{figure(stats.completed)} done</span>,
    ...(toBill > 0 ? [<span key="to-bill" className="text-warning"><span className="font-semibold tabular-nums">{toBill}</span> not paid</span>] : []),
  ];

  return (
    <div className="kb-fit-screen h-[calc(100dvh-var(--topbar-height,60px))] flex flex-col overflow-hidden">

      {/* ── Header: one status line, then the doctor filter ─────────────────── */}
      <div className="flex-none border-b border-border bg-background">
        <div className="flex items-end justify-between gap-3 px-4 pt-3 pb-2">
          <div className="min-w-0">
            <h1 className="text-xl font-semibold text-foreground">Front desk</h1>
            <p className="mt-0.5 flex flex-wrap items-baseline gap-x-1.5 gap-y-0.5 text-sm text-muted-foreground">
              {statusParts.map((part, i) => (
                <React.Fragment key={i}>{i > 0 && <span aria-hidden>·</span>}{part}</React.Fragment>
              ))}
            </p>
          </div>

          {/* Phones: refresh + menu */}
          <div className="flex md:hidden items-center gap-1">
            <Button variant="ghost" size="sm" className="h-8 w-8 p-0" onClick={handleRefresh} disabled={refreshing} aria-label="Refresh">
              <RefreshCw className={cn('w-4 h-4', refreshing && 'animate-spin')} />
            </Button>
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="ghost" size="sm" className="h-8 w-8 p-0" aria-label="More">
                  <MoreHorizontal className="w-4 h-4" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                <DropdownMenuItem onClick={() => navigate('/frontdesk/reports')}>Reports</DropdownMenuItem>
                <DropdownMenuItem onClick={() => window.open('/token-display', '_blank')}>Token display ↗</DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>

          <div className="hidden md:flex items-center gap-2">
            <Button variant="outline" size="sm" className="w-8 px-0" onClick={handleRefresh} disabled={refreshing} title="Refresh" aria-label="Refresh">
              <RefreshCw className={cn('w-4 h-4', refreshing && 'animate-spin')} />
            </Button>
            <Button variant="outline" size="sm" className="gap-1.5" onClick={() => navigate('/frontdesk/reports')}>
              <BarChart3 className="w-4 h-4" /> Reports
            </Button>
            <Button variant="outline" size="sm" className="gap-1.5" onClick={() => window.open('/token-display', '_blank')} title="Open token display screen (for TV/monitor)">
              <Monitor className="w-4 h-4" /> Token display ↗
            </Button>
          </div>
        </div>

        {/* Doctor filter — same look as the other segmented filters */}
        {doctors.length > 1 && (
          <div className="px-4 pb-2.5 overflow-x-auto scrollbar-subtle">
            <div role="group" aria-label="Doctor" className="inline-flex items-center h-8 touch:h-10 rounded-md bg-muted p-0.5 gap-0.5">
              {[{ id: 'all', name: '' }, ...tabDoctors].map(d => {
                const isActive = selectedDoctorId === d.id;
                const waitCount = d.id === 'all'
                  ? 0
                  : queues[d.id]?.entries.filter(e => e.status === 'waiting').length ?? 0;
                return (
                  <button
                    key={d.id}
                    type="button"
                    aria-pressed={isActive}
                    onClick={() => setSelectedDoctorId(d.id)}
                    className={cn(
                      'h-7 touch:h-9 inline-flex items-center gap-1.5 px-3 rounded text-xs font-medium whitespace-nowrap transition-colors',
                      isActive ? 'bg-background text-foreground shadow-sm' : 'text-muted-foreground hover:text-foreground',
                    )}
                  >
                    {d.id === 'all' ? 'All doctors' : drName(d.name)}
                    {waitCount > 0 && (
                      <span className="min-w-4 h-4 px-1 rounded bg-highlight text-2xs font-semibold tabular-nums text-highlight-foreground flex items-center justify-center" aria-label={`${waitCount} waiting`}>
                        {waitCount}
                      </span>
                    )}
                  </button>
                );
              })}
            </div>
          </div>
        )}
      </div>

      {/* ── Two-panel body ──────────────────────────────────────────────────── */}
      <div className="flex-1 flex min-h-0 overflow-hidden">

        {/* ── Left: Queue list ─────────────────────────────────────────────── */}
        {/* Phones show one pane at a time: the queue, or the selected patient */}
        <div className={`${selected ? 'hidden md:flex' : 'flex'} w-full md:w-64 lg:w-80 flex-none flex-col md:border-r border-border bg-card overflow-hidden`}>

          {/* Search / add */}
          {can('queue:write') && (
            <div className="flex-none border-b border-border/60">
              <QueueAddSearch
                doctors={doctors}
                effectiveDoctorId={effectiveDoctorId}
                clinicId={clinicId}
                onAdded={() => fetchQueues(doctors)}
                onRegisterNew={can('patients:write') ? handleRegisterNew : undefined}
              />
            </div>
          )}

          {/* Scrollable list */}
          <div className="flex-1 overflow-y-auto">

            {/* In Consultation */}
            {inConsultation.length > 0 && (
              <>
                <SectionHeader
                  label="In consultation"
                />
                {inConsultation.map(entry => (
                  <QueueRow
                    key={entry.id}
                    entry={entry}
                    selected={isSelectedEntry(entry.id)}
                    showDoctor={selectedDoctorId === 'all'}
                    onClick={() => setSelected({ kind: 'queue', entry })}
                    confirmingRemove={false}
                    onRequestRemove={() => {}}
                    onConfirmRemove={() => {}}
                    onCancelRemove={() => {}}
                  />
                ))}
              </>
            )}

            {/* On hold — sent for a test, coming back to the same doctor */}
            {onHold.length > 0 && (
              <>
                <SectionHeader
                  label="On hold" count={onHold.length}
                />
                {onHold.map(entry => (
                  <QueueRow
                    key={entry.id}
                    entry={entry}
                    selected={isSelectedEntry(entry.id)}
                    showDoctor={selectedDoctorId === 'all'}
                    onClick={() => setSelected({ kind: 'queue', entry })}
                    confirmingRemove={confirmRemoveId === entry.id}
                    onRequestRemove={() => setConfirmRemoveId(entry.id)}
                    onConfirmRemove={() => handleRemoveFromQueue(entry)}
                    onCancelRemove={() => setConfirmRemoveId(null)}
                  />
                ))}
              </>
            )}

            {/* Waiting */}
            {waiting.length > 0 && (
              <>
                <SectionHeader
                  label="Waiting" count={waiting.length}
                  action={can('queue:write') && selectedDoctorId !== 'all' && doctors.length > 1 ? (
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild>
                        <button type="button" className="text-2xs font-medium text-primary hover:underline underline-offset-2">Move all</button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent align="end">
                        {doctors.filter(d => d.id !== selectedDoctorId).map(d => (
                          <DropdownMenuItem key={d.id} onClick={() => handleMoveAll(d.id)}>To {drName(d.name)}</DropdownMenuItem>
                        ))}
                      </DropdownMenuContent>
                    </DropdownMenu>
                  ) : undefined}
                />
                {waiting.map(entry => (
                  <QueueRow
                    key={entry.id}
                    entry={entry}
                    selected={isSelectedEntry(entry.id)}
                    showDoctor={selectedDoctorId === 'all'}
                    onClick={() => setSelected({ kind: 'queue', entry })}
                    confirmingRemove={confirmRemoveId === entry.id}
                    onRequestRemove={() => setConfirmRemoveId(entry.id)}
                    onConfirmRemove={() => handleRemoveFromQueue(entry)}
                    onCancelRemove={() => setConfirmRemoveId(null)}
                  />
                ))}
              </>
            )}

            {/* Upcoming appointments */}
            {upcoming.length > 0 && (
              <>
                <SectionHeader
                  label="Schedule" count={upcoming.length}
                />
                {upcoming.map(apt => (
                  <ApptRow
                    key={apt.id}
                    apt={apt}
                    selected={isSelectedApt(apt.id)}
                    checkingIn={checkingInId === apt.id}
                    showDoctor={selectedDoctorId === 'all'}
                    doctors={doctors}
                    onSelect={() => setSelected({ kind: 'appointment', apt })}
                    onCheckIn={can('queue:write') ? () => handleCheckIn(apt) : undefined}
                  />
                ))}
              </>
            )}

            {/* Completed */}
            {completed.length > 0 && (
              <>
                <SectionHeader
                  label="Done" count={completed.length}
                />
                {shownDone.map(entry => (
                  <QueueRow
                    key={entry.id}
                    entry={entry}
                    selected={isSelectedEntry(entry.id)}
                    showDoctor={selectedDoctorId === 'all'}
                    onClick={() => setSelected({ kind: 'queue', entry })}
                    confirmingRemove={false}
                    onRequestRemove={() => {}}
                    onConfirmRemove={() => {}}
                    onCancelRemove={() => {}}
                    dim
                    bill={billOf(entry)}
                  />
                ))}
                {(showAllDone || shownDone.length < completed.length) && (
                  <button
                    type="button"
                    onClick={() => setShowAllDone(v => !v)}
                    className="w-full h-9 px-3 text-left text-xs text-muted-foreground hover:text-foreground hover:bg-muted/40 transition-colors border-b border-divider"
                  >
                    {showAllDone ? 'Show fewer' : `Show ${completed.length - shownDone.length} more`}
                  </button>
                )}
              </>
            )}

            {/* Empty state */}
            {inConsultation.length === 0 && onHold.length === 0 && waiting.length === 0 && upcoming.length === 0 && completed.length === 0 && (
              <div className="flex flex-col items-center justify-center py-16 text-center px-4">
                <div className="w-11 h-11 rounded-full bg-muted flex items-center justify-center mb-3">
                  <LayoutDashboard className="w-5 h-5 text-muted-foreground" />
                </div>
                <p className="text-sm font-medium text-foreground">Queue is empty</p>
                <p className="text-xs text-muted-foreground mt-1 leading-relaxed">
                  Search for a patient above<br />to add them to the queue
                </p>
              </div>
            )}

            {/* Follow-ups who didn't come back: call or WhatsApp them, and book them in */}
            <MissedFollowUps
              followUps={shownMissed}
              showDoctor={selectedDoctorId === 'all'}
              onBook={can('appointments:write') ? f => setBookPatient({ id: f.patient_id, name: f.patient_name, doctorId: f.doctor_id }) : undefined}
            />
          </div>
        </div>

        {/* ── Right: Patient context panel ─────────────────────────────────── */}
        <div className={`${selected ? 'block' : 'hidden md:block'} flex-1 min-w-0 overflow-y-auto`}>
          {selected && (
            <div className="md:hidden sticky top-0 z-10 bg-background border-b border-border px-3 py-2">
              <button onClick={() => setSelected(null)} className="flex items-center gap-1.5 text-sm font-medium text-primary">
                <ArrowLeft className="w-4 h-4" /> Queue
              </button>
            </div>
          )}
          {selectedPatientId ? (
            <PatientContextPanel
              key={`${selectedPatientId}:${panelVersion}`}
              patientId={selectedPatientId}
              patientName={selectedPatientName}
              queueEntry={selectedQueueEntry}
              onNewInvoice={openNewInvoice}
              onCollectPayment={setCollectInvoice}
              onBookAppointment={setBookPatient}
              onEditPatient={setEditPatient}
              onPrintPrescription={setPrintRx}
              onPrintInvoice={setPrintInvoice}
              onPriority={handlePriority}
              onRemoveFromQueue={handleRemoveFromQueue}
              onReturnWithReport={e => handleReturnWithReport(e as QueueEntryEx)}
              moveTargets={selectedQueueEntry ? doctors.filter(d => d.id !== selectedQueueEntry.doctorId) : []}
              onMove={can('queue:write') ? handleMove : undefined}
              onViewFullHistory={setDrawerPatientId}
              onVitalsRecorded={() => fetchQueues(doctors)}
            />
          ) : (
            <div className="flex flex-col items-center justify-center h-full text-center px-8 gap-3">
              <div className="w-11 h-11 rounded-full bg-muted flex items-center justify-center">
                <Users className="w-5 h-5 text-muted-foreground" />
              </div>
              <div>
                <p className="text-sm font-medium text-foreground">No patient selected</p>
                <p className="text-xs text-muted-foreground mt-1">
                  Click any patient in the queue to see details
                </p>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* ── Patient history drawer ──────────────────────────────────────────── */}
      {drawerPatientId && (
        <PatientDrawer
          patientId={drawerPatientId}
          onClose={() => setDrawerPatientId(null)}
          onCollectPayment={inv => { setDrawerPatientId(null); setCollectInvoice(inv); }}
          onPrintPrescription={setPrintRx}
          onPrintInvoice={setPrintInvoice}
        />
      )}

      {/* ── Modals ──────────────────────────────────────────────────────────── */}

      {billingPatient && (
        <MedicalBillingModal
          isOpen
          onClose={() => setBillingPatient(null)}
          patient={billingPatient}
          services={billingServices}
          onCreateInvoice={handleCreateInvoice}
          loading={billingLoading}
          clinicId={clinicId}
          doctorId={
            selectedQueueEntry?.doctorId ?? (selectedDoctorId !== 'all' ? selectedDoctorId : doctors[0]?.id ?? currentUser?.doctor_id ?? '')
          }
          doctors={doctors}
        />
      )}

      <PrintDepositReceiptModal open={!!depositReceipt} deposit={depositReceipt} onClose={() => setDepositReceipt(null)} />

      <PaymentRecordModal
        isOpen={!!collectInvoice}
        onClose={() => setCollectInvoice(null)}
        invoice={collectInvoice}
        onRecordPayment={handleCollectPayment}
        loading={false}
      />

      {editPatient && (
        <PatientFormModal
          isOpen
          onClose={() => setEditPatient(null)}
          onSuccess={async () => {
            setEditPatient(null);
            await loadAll();
          }}
          mode="edit"
          patientId={editPatient.id}
          initialData={{
            name: editPatient.name ?? '',
            age: editPatient.age?.toString() ?? '',
            gender: editPatient.gender ?? '',
            phone: editPatient.phone ?? '',
            phone_2: editPatient.phone_2 ?? '',
            email: editPatient.email ?? '',
            address: editPatient.address ?? '',
            city: editPatient.city ?? '',
            pin_code: editPatient.pin_code ?? '',
            blood_group: editPatient.blood_group ?? '',
            allergies: editPatient.allergies ?? '',
            chronic_conditions: editPatient.chronic_conditions ?? '',
            current_medications: editPatient.current_medications ?? '',
            date_of_birth: editPatient.date_of_birth ?? '',
            preferred_language: editPatient.preferred_language ?? 'English',
            aadhar_number: editPatient.aadhar_number ?? '',
            emergency_contact_name: editPatient.emergency_contact_name ?? '',
            emergency_contact_phone: editPatient.emergency_contact_phone ?? '',
            payment_preference: editPatient.payment_preference ?? 'Cash',
          }}
          showPrescriptionOption={false}
          showQueueOption={false}
          title="Edit Patient"
        />
      )}

      {bookPatient && (
        <AddAppointmentModal
          isOpen
          onClose={() => setBookPatient(null)}
          patientId={bookPatient.id}
          patients={bookPatient ? [bookPatient] : []}
          showPatientSelection={false}
          initialDoctorId={bookPatient.doctorId ?? selectedQueueEntry?.doctorId}
          onAdded={() => {
            setBookPatient(null);
            loadAll();
          }}
        />
      )}

      <PatientFormModal
        isOpen={showRegisterModal}
        onClose={() => setShowRegisterModal(false)}
        onSuccess={handleRegisterSuccess}
        title="New patient"
        showPrescriptionOption={false}
        showQueueOption={false}
        initialSearch={registerSearch}
      />

      {printRx && (
        <PrintPrescriptionPreviewModal
          open={!!printRx}
          prescription={printRx}
          onClose={() => setPrintRx(null)}
        />
      )}

      {printInvoice && (
        <PrintBillPreviewModal
          open={!!printInvoice}
          invoice={printInvoice}
          onClose={() => setPrintInvoice(null)}
        />
      )}
    </div>
  );
}

// ─── Section header ───────────────────────────────────────────────────────────

function SectionHeader({ label, count, action }: { label: string; count?: number; action?: React.ReactNode }) {
  return (
    <div className="flex items-center justify-between h-8 px-3 border-b border-border bg-muted/40">
      <span className="text-2xs font-medium uppercase tracking-wide text-muted-foreground">{label}</span>
      <span className="flex items-center gap-3">
        {action}
        {count != null && <span className="text-2xs font-medium tabular-nums text-muted-foreground">{count}</span>}
      </span>
    </div>
  );
}

// ─── Queue row ────────────────────────────────────────────────────────────────

interface QueueRowProps {
  entry: QueueEntryEx;
  selected: boolean;
  showDoctor: boolean;
  onClick: () => void;
  confirmingRemove: boolean;
  onRequestRemove: () => void;
  onConfirmRemove: () => void;
  onCancelRemove: () => void;
  dim?: boolean;
  /** Done rows: whether today's bill is paid */
  bill?: BillState;
}

/** A seen patient's bill today: paid, the amount still due, or no bill yet */
type BillState = 'paid' | 'unbilled' | number;

function QueueRow({
  entry, selected, showDoctor, onClick, dim, bill,
  confirmingRemove, onRequestRemove, onConfirmRemove, onCancelRemove,
}: QueueRowProps) {
  const isConsult = entry.status === 'in_consultation';
  const isDone    = entry.status === 'completed';
  const isHold    = entry.status === 'on_hold';
  const isWaiting = entry.status === 'waiting' || isHold;
  // The "Vitals" tag only when all readings are normal — warning chips already say vitals were taken
  const vitalsDone = !!entry.vitalsRecordedAt && vitalsWarnings(entry).length === 0;
  const isLong    = (entry.waitingTimeMinutes ?? 0) >= 30;
  const isMedium  = !isLong && (entry.waitingTimeMinutes ?? 0) >= 15;

  return (
    <div
      role="button"
      tabIndex={0}
      aria-pressed={selected}
      className={cn(
        'group relative flex items-center gap-2.5 px-3 py-2 min-h-12 border-b border-divider border-l-[3px] transition-colors cursor-pointer',
        'focus-visible:outline-none focus-visible:bg-muted/60',
        selected ? 'bg-primary/5 border-l-primary' : 'hover:bg-muted/50 border-l-transparent',
      )}
      onClick={confirmingRemove ? undefined : onClick}
      onKeyDown={e => { if (!confirmingRemove && e.target === e.currentTarget && (e.key === 'Enter' || e.key === ' ')) { e.preventDefault(); onClick(); } }}
    >
      {/* Token — fill shows who is in, and how long the others have waited */}
      <div className={cn(
        'w-9 h-8 rounded flex items-center justify-center font-mono text-sm font-semibold flex-shrink-0 tabular-nums',
        isConsult ? 'bg-primary text-primary-foreground' :
        isDone    ? 'bg-muted text-muted-foreground' :
        isLong    ? 'bg-[color-mix(in_srgb,hsl(var(--destructive))_10%,hsl(var(--card)))] text-destructive' :
        isMedium  ? 'bg-highlight text-highlight-foreground' :
                    'bg-muted text-foreground',
      )}>
        {entry.tokenNumber}
      </div>

      {confirmingRemove ? (
        <div className="flex-1 min-w-0 flex items-center justify-between gap-2" onClick={e => e.stopPropagation()}>
          <span className="text-xs text-destructive font-medium truncate">Remove {entry.patientName.split(' ')[0]}?</span>
          <div className="flex gap-1 flex-shrink-0">
            <button
              onClick={onConfirmRemove}
              className="h-7 px-2.5 rounded text-xs font-medium bg-destructive text-destructive-foreground hover:bg-destructive/90 transition-colors"
            >
              Remove
            </button>
            <button
              onClick={onCancelRemove}
              className="h-7 px-2.5 rounded text-xs font-medium border border-border text-foreground hover:bg-muted transition-colors"
            >
              Keep
            </button>
          </div>
        </div>
      ) : (
        <>
          <div className="flex-1 min-w-0">
            <p className="flex items-center gap-1.5 min-w-0">
              <span className={cn('text-sm font-medium truncate capitalize', isDone || dim ? 'text-muted-foreground' : 'text-foreground')}>
                {entry.patientName}
              </span>
              {vitalsDone && !isDone && (
                <StatusChip tone="success" className="flex-shrink-0">Vitals</StatusChip>
              )}
            </p>
            {!isDone && <VitalsWarningChips entry={entry} max={2} className="mt-0.5" />}
            <p className={cn('text-xs truncate mt-0.5',
              isConsult ? 'text-primary font-medium' :
              isDone    ? 'text-muted-foreground' :
                          waitColor(entry.waitingTimeMinutes) || 'text-muted-foreground',
            )}>
              {isConsult
                ? `In consultation${showDoctor ? ` · ${drName(entry.doctorName)}` : ''}`
                : isHold
                ? `On hold · test${showDoctor ? ` · ${drName(entry.doctorName)}` : ''}`
                : isDone
                ? <>
                    {bill === 'paid' ? <span className="text-success">Paid</span>
                      : bill === 'unbilled' ? <span className="text-warning font-medium">Not billed</span>
                      : typeof bill === 'number' ? <span className="text-warning font-medium">₹{bill.toLocaleString('en-IN')} due</span>
                      : 'Done'}
                    {showDoctor ? ` · ${drName(entry.doctorName)}` : ''}
                  </>
                : [
                    isReportReturn(entry) && 'Report',
                    entry.waitingTimeMinutes != null && (entry.waitingTimeMinutes > 0 ? `Waiting ${formatWait(entry.waitingTimeMinutes)}` : 'Just arrived'),
                    showDoctor && drName(entry.doctorName),
                  ].filter(Boolean).join(' · ')
              }
            </p>
          </div>

          {/* Remove button — visible on hover for waiting entries */}
          {isWaiting && (
            <button
              onClick={e => { e.stopPropagation(); onRequestRemove(); }}
              className="[@media(hover:hover)]:opacity-0 group-hover:opacity-100 focus-visible:opacity-100 h-7 w-7 flex items-center justify-center rounded text-muted-foreground hover:text-destructive hover:bg-destructive/10 transition-colors flex-shrink-0"
              title="Remove from queue"
              aria-label={`Remove ${entry.patientName} from queue`}
            >
              <X className="w-3.5 h-3.5" />
            </button>
          )}
        </>
      )}
    </div>
  );
}

// ─── Appointment row ──────────────────────────────────────────────────────────

interface ApptRowProps {
  apt: AppointmentRow;
  selected: boolean;
  checkingIn: boolean;
  showDoctor: boolean;
  doctors: Doctor[];
  onSelect: () => void;
  onCheckIn?: () => void;
}

function ApptRow({ apt, selected, checkingIn, showDoctor, doctors, onSelect, onCheckIn }: ApptRowProps) {
  const docName = showDoctor && apt.doctor_id
    ? doctors.find(d => d.id === apt.doctor_id)?.name
    : null;
  const timeLabel = formatTime(apt.time);
  const dueSoon = isDueSoon(apt.time);

  return (
    <div
      role="button"
      tabIndex={0}
      aria-pressed={selected}
      className={cn(
        'flex items-center gap-2.5 px-3 py-2 min-h-12 border-b border-divider border-l-[3px] transition-colors cursor-pointer',
        'focus-visible:outline-none focus-visible:bg-muted/60',
        selected ? 'bg-primary/5 border-l-primary' : 'hover:bg-muted/50 border-l-transparent',
      )}
      onClick={onSelect}
      onKeyDown={e => { if (e.target === e.currentTarget && (e.key === 'Enter' || e.key === ' ')) { e.preventDefault(); onSelect(); } }}
    >
      {/* Time */}
      <span className={cn('w-9 flex-shrink-0 font-mono text-xs leading-tight tabular-nums text-center', dueSoon ? 'text-foreground font-semibold' : 'text-muted-foreground')}>
        {timeLabel.split(' ')[0]}<span className="block text-2xs font-sans font-medium uppercase">{timeLabel.split(' ')[1]}</span>
      </span>

      <div className="flex-1 min-w-0">
        <p className="text-sm font-medium text-foreground truncate capitalize">{apt.patientName}</p>
        <p className="text-xs text-muted-foreground truncate mt-0.5">
          {[apt.reason || 'Appointment', docName && drName(docName)].filter(Boolean).join(' · ')}
        </p>
      </div>

      {onCheckIn && (
        <Button
          size="sm"
          variant={dueSoon ? 'default' : 'outline'}
          disabled={checkingIn}
          onClick={e => { e.stopPropagation(); onCheckIn(); }}
          className="h-7 px-2.5 text-xs flex-shrink-0"
        >
          {checkingIn ? 'Checking in…' : 'Check in'}
        </Button>
      )}
    </div>
  );
}
